import { createStore, del, delMany, get, keys, set } from "idb-keyval";
import type { KeyCopy, ManifestFile } from "@timecapsule/crypto";

/**
 * Everything the device keeps for itself, always per member (several members may share a
 * browser): the device copy of the key, opened text (so a capsule is fetched from drand once),
 * notes about sent capsules and the keys last seen in the directory.
 */
const store = createStore("timecapsule", "kv");

/**
 * Device copy of the member's key (PLAN.md 4.6). "prf-pin": encrypted with passkey PRF + PIN,
 * needs no server. "plain": browsers without passkey PRF keep the key unencrypted, and the
 * member is told so.
 */
export type DeviceKey = { kind: "prf-pin"; pubkey: string; copy: KeyCopy } | { kind: "plain"; pubkey: string; secretKey: Uint8Array };

const keyName = (memberId: number) => `key2:${memberId}`;

export const deviceKey = {
  load: (memberId: number) => get<DeviceKey>(keyName(memberId), store),
  save: (memberId: number, record: DeviceKey) => set(keyName(memberId), record, store),
};

// ---------- opened capsules (PLAN.md 4.11 H8) ----------

/** What opening a capsule yields: the text and the manifest's file entries (with their keys). */
export type Opened = { text: string; files: ManifestFile[] };

/**
 * A public capsule is public once it unlocks, so it is cached as is. A private one is cached
 * encrypted with a key derived from the member's secret (HKDF info "tc-cache-v1", AES-256-GCM,
 * the event id as additional data) and can be read back only while the key is unlocked; this also
 * protects the file keys in its manifest.
 */
type CachedText = { t: "public"; json: string } | { t: "private"; iv: Uint8Array; data: Uint8Array };

function parseOpened(json: string): Opened | null {
  try {
    const o = JSON.parse(json) as Opened;
    return typeof o.text === "string" && Array.isArray(o.files) ? o : null;
  } catch {
    return null;
  }
}

const utf8 = new TextEncoder();
const cacheKeys = new WeakMap<Uint8Array, Promise<CryptoKey>>();

function cacheKey(secretKey: Uint8Array): Promise<CryptoKey> {
  let key = cacheKeys.get(secretKey);
  if (!key) {
    key = crypto.subtle
      .importKey("raw", secretKey.slice(), "HKDF", false, ["deriveKey"])
      .then((base) =>
        crypto.subtle.deriveKey(
          { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(0), info: utf8.encode("tc-cache-v1") },
          base,
          { name: "AES-GCM", length: 256 },
          false,
          ["encrypt", "decrypt"],
        ),
      );
    cacheKeys.set(secretKey, key);
  }
  return key;
}

const textName = (memberId: number, eventId: string) => `plain:${memberId}:${eventId}`;

export const openedText = {
  /** Null when not cached, or when it is private and the key is not unlocked. */
  async load(memberId: number, eventId: string, secretKey: Uint8Array | null): Promise<Opened | null> {
    const cached = await get<CachedText>(textName(memberId, eventId), store);
    if (!cached) return null;
    if (cached.t === "public") return typeof cached.json === "string" ? parseOpened(cached.json) : null;
    if (!secretKey) return null;
    try {
      const plain = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: cached.iv.slice(), additionalData: utf8.encode(eventId) },
        await cacheKey(secretKey),
        cached.data.slice(),
      );
      return parseOpened(new TextDecoder().decode(plain));
    } catch {
      return null; // cached under another identity of this member
    }
  },
  async save(memberId: number, eventId: string, opened: Opened, privateWith: Uint8Array | null): Promise<void> {
    const text = JSON.stringify(opened);
    if (!privateWith) return set(textName(memberId, eventId), { t: "public", json: text } satisfies CachedText, store);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const data = new Uint8Array(
      await crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: utf8.encode(eventId) }, await cacheKey(privateWith), utf8.encode(text)),
    );
    await set(textName(memberId, eventId), { t: "private", iv, data } satisfies CachedText, store);
  },
};

/**
 * A private wrap is encrypted to its recipient, so without "keep a copy" its author cannot read
 * the unlock round back from the server. The sending device remembers it here, written only
 * after the server accepted the capsule.
 */
export type SentPrivate = { recipient: string; round: number; createdAt: number };

export const sentPrivate = {
  load: (memberId: number, wrapId: string) => get<SentPrivate>(`sent:${memberId}:${wrapId}`, store),
  save: (memberId: number, wrapId: string, meta: SentPrivate) => set(`sent:${memberId}:${wrapId}`, meta, store),
};

/**
 * The last active pubkey this browser saw for each member in the directory (PLAN.md 4.11 H1).
 * Compose warns when the directory now shows another one; the record changes only when the
 * member confirms (sends after the warning, or "I checked").
 */
export type SeenKey = { pubkey: string; since: number | null };

export const seenKeys = {
  load: (me: number, memberId: number) => get<SeenKey>(`seen:${me}:${memberId}`, store),
  save: (me: number, memberId: number, seen: SeenKey) => set(`seen:${me}:${memberId}`, seen, store),
};

/**
 * "Forget this device" (PLAN.md 4.11 H8): deletes this member's device key copy, opened text,
 * sent notes and remembered directory keys. Also removes records in the pre-4a formats, which
 * had no member in their name.
 */
export async function forgetDevice(memberId: number): Promise<void> {
  const mine = [`key2:${memberId}`, `plain:${memberId}:`, `sent:${memberId}:`, `seen:${memberId}:`];
  const legacy = (k: string) => /^(key:|copy:)/.test(k) || /^(plain|sent):[0-9a-f]{64}$/.test(k);
  const all = (await keys(store)).map(String);
  await delMany(
    all.filter((k) => mine.some((p) => k === p || (p.endsWith(":") && k.startsWith(p))) || legacy(k)),
    store,
  );
  await del(keyName(memberId), store);
}

